import { ComponentProps, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  UpdateExtrasRequest,
  useVbsPricingShowQuery,
  useVbsPricingUpdateExtrasMutation,
} from "@/services/backend/vbs/pricing/api/service";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import {
  copyExtra,
  Extra,
  newExtra,
  Pricing,
} from "@/services/backend/vbs/pricing/types/pricing";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { GripVertical } from "lucide-react";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellInput,
  ETableHead,
  ETableHeader,
  ETableOptions,
  ETableRow,
  setOrderFieldToIndex,
} from "@/shared/components/tables/e-table";

export function ExtrasCard({
  pricingId,
  disabled = false,
  ...props
}: {
  pricingId: string;
  disabled?: boolean;
} & ComponentProps<typeof Card>) {
  const { data, isLoading, error } = useVbsPricingShowQuery({ id: pricingId });

  if (error) {
    return <RTKQueryErrorAlert error={error} ignoreValidationError={false} />;
  }

  return (
    <Card {...props}>
      <CardHeader>
        <CardTitle>{t("Zusatzleistungen")}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <CardContentSkeleton />}
        {data && <ExtrasComponent pricing={data} disabled={disabled} />}
      </CardContent>
    </Card>
  );
}

function ExtrasComponent({
  pricing,
  disabled = false,
}: {
  pricing: Pricing;
  disabled?: boolean;
}) {
  const [request, setRequest] = useState<UpdateExtrasRequest>({
    id: pricing.id,
    extras: pricing.extras,
  });
  const mutation = useVbsPricingUpdateExtrasMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: pricing.id,
    extras: pricing.extras,
  });

  return (
    <ExtrasTable
      extras={request.extras}
      onExtrasChange={(extras) => setRequest({ ...request, extras })}
      disabled={disabled}
    />
  );
}

function ExtrasTable({
  extras,
  onExtrasChange,
  disabled,
}: {
  extras: Extra[];
  onExtrasChange: (extras: Extra[]) => void;
  disabled?: boolean;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    if (disabled) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = extras.findIndex((e) => e.id === active.id);
    const newIndex = extras.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onExtrasChange(setOrderFieldToIndex(arrayMove(extras, oldIndex, newIndex)));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={extras.map((e) => e.id)}
        strategy={verticalListSortingStrategy}
      >
        <ETable>
          <ETableHeader>
            <ETableRow>
              <ETableHead />
              <ETableHead className="w-full">{t("Name")}</ETableHead>
              <ETableHead>{t("Einheit")}</ETableHead>
              <ETableHead>{t("Preis pro Einheit")}</ETableHead>
              <ETableHead />
            </ETableRow>
          </ETableHeader>
          <ETableBody>
            {extras.map((extra, index) => (
              <ExtraRow
                key={extra.id}
                index={index}
                extra={extra}
                onExtraChange={(updated) =>
                  onExtrasChange(
                    extras.map((e) => (e.id === updated.id ? updated : e)),
                  )
                }
                extras={extras}
                onExtrasChange={onExtrasChange}
                disabled={disabled}
              />
            ))}
          </ETableBody>
        </ETable>
      </SortableContext>
    </DndContext>
  );
}

function ExtraRow({
  extra,
  onExtraChange,
  extras,
  onExtrasChange,
  index,
  disabled,
}: {
  extra: Extra;
  onExtraChange: (update: Extra) => void;
  extras: Extra[];
  onExtrasChange: (extras: Extra[]) => void;
  index: number;
  disabled?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: extra.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ETableRow ref={setNodeRef} style={style}>
      <ETableCell className="w-10">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="hover:bg-muted cursor-grab rounded p-1 disabled:cursor-default disabled:opacity-50"
          disabled={disabled}
        >
          <GripVertical className="text-muted-foreground h-4 w-4" />
        </button>
      </ETableCell>
      <ETableCellInput
        value={extra.name}
        onChange={(e) => onExtraChange({ ...extra, name: e.target.value })}
        disabled={disabled}
        placeholder={t("Name")}
      />
      <ETableCellInput
        value={extra.unit}
        onChange={(e) => onExtraChange({ ...extra, unit: e.target.value })}
        placeholder={t("Einheit")}
        disabled={disabled}
      />
      <ETableCellInput
        type="number"
        value={extra.pricePerUnit}
        onChange={(e) =>
          onExtraChange({ ...extra, pricePerUnit: Number(e.target.value) })
        }
        placeholder={t("Preis")}
        className="text-right"
        disabled={disabled}
      />
      <ETableCell>
        <ETableOptions
          item={extra}
          rowIndex={index}
          items={extras}
          onItemsChange={onExtrasChange}
          newItem={() => newExtra(extra.pricingId)}
          duplicate={(e: Extra) => copyExtra(e)}
          disabled={disabled}
        />
      </ETableCell>
    </ETableRow>
  );
}
